/**
 * Public vendor sign-up page (Feb 2026).
 *
 * Reached via a unique link emailed by the landlord:
 *   /vendor-signup/{token}
 *
 * Validates the token first to avoid showing the form for expired or
 * already-consumed links. On submit, creates a vendor record under the
 * landlord's account — the landlord is notified by email.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Briefcase, CheckCircle2, Loader2, ShieldCheck, AlertTriangle } from "lucide-react";
import AddressAutocomplete from "../components/AddressAutocomplete";
import USPhoneInput from "../components/USPhoneInput";

const API = process.env.REACT_APP_BACKEND_URL;

const ENTITY_TYPES = [
  { value: "ein", label: "EIN (Business)" },
  { value: "ssn", label: "SSN (Individual)" },
];

function formatTaxId(raw, type) {
  const digits = (raw || "").replace(/\D/g, "").slice(0, 9);
  if (!digits) return "";
  if (type === "ssn") {
    if (digits.length <= 3) return digits;
    if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
  }
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}-${digits.slice(2)}`;
}

export default function VendorSignup() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [linkInfo, setLinkInfo] = useState(null);
  const [linkError, setLinkError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    contact_phone: "",
    category: "",
    mailing_address: "",
    tax_id: "",
    tax_id_type: "ein",
    notes: "",
  });

  useEffect(() => {
    let canceled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/public/vendor-signup/${token}`);
        if (!canceled) {
          setLinkInfo(data);
          setForm((f) => ({
            ...f,
            email: data.invitee_email || "",
            name: data.invitee_company || "",
          }));
        }
      } catch (err) {
        if (!canceled) {
          setLinkError(err.response?.data?.detail || "This link is no longer valid");
        }
      } finally {
        if (!canceled) setLoading(false);
      }
    })();
    return () => { canceled = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Legal name is required");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/public/vendor-signup/${token}`, {
        ...form,
        tax_id: formatTaxId(form.tax_id, form.tax_id_type),
      });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed — please try again");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (linkError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-sm p-8 text-center" data-testid="vendor-signup-error">
          <AlertTriangle className="w-10 h-10 mx-auto text-amber-500 mb-3" />
          <h1 className="text-lg font-bold text-slate-900 mb-2">Sign-up link unavailable</h1>
          <p className="text-sm text-slate-600">{linkError}</p>
          <p className="text-xs text-slate-400 mt-4">If you believe this is a mistake, please contact the landlord who sent you this link.</p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-sm p-8 text-center" data-testid="vendor-signup-done">
          <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-500 mb-3" />
          <h1 className="text-lg font-bold text-slate-900 mb-2">All done — thank you!</h1>
          <p className="text-sm text-slate-600">
            Your vendor profile has been submitted to <b>{linkInfo?.landlord_label}</b>.
            You'll be added to their vendor list and will receive payment notifications by email.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <Toaster position="top-right" richColors />
      <div className="max-w-2xl mx-auto">
        <header className="mb-6 flex items-start gap-3">
          <div className="w-10 h-10 bg-slate-900 text-white rounded-sm flex items-center justify-center shrink-0">
            <Briefcase className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900 tracking-tight">Vendor sign-up</h1>
            <p className="text-sm text-slate-600">
              <b>{linkInfo?.landlord_label}</b> has invited you to join their vendor list. Complete the form below — it takes about 2 minutes.
            </p>
            {linkInfo?.note && (
              <div className="mt-3 p-3 bg-amber-50 border-l-2 border-amber-300 text-xs text-amber-900 rounded-sm">
                <b>Personal note:</b> {linkInfo.note}
              </div>
            )}
          </div>
        </header>

        <form onSubmit={submit} className="bg-white border border-slate-200 rounded-sm p-6 space-y-4">
          <Field label="Legal name (company or individual)" required>
            <input
              required
              className="input-clean w-full"
              data-testid="vsu-name-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. ACME Plumbing LLC"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Contact email">
              <input
                type="email"
                className="input-clean w-full"
                data-testid="vsu-email-input"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </Field>
            <Field label="Phone">
              <USPhoneInput
                value={form.contact_phone}
                onChange={(d) => setForm({ ...form, contact_phone: d })}
                data-testid="vsu-phone-input"
              />
            </Field>
          </div>
          <Field label="Service category">
            <input
              className="input-clean w-full"
              data-testid="vsu-category-input"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              placeholder="e.g. Plumbing / Electrical / HVAC / Landscaping"
            />
          </Field>
          <Field label="Mailing address" hint="Where the landlord will mail your 1099-NEC each January">
            <AddressAutocomplete
              value={form.mailing_address}
              onChange={(v) => setForm({ ...form, mailing_address: v })}
              onVerified={({ formatted_address }) => setForm({ ...form, mailing_address: formatted_address })}
              placeholder="Start typing — Google will suggest"
              dataTestId="vsu-address-input"
            />
          </Field>
          <div>
            <div className="overline text-slate-500 mb-1">Tax ID — for IRS Form 1099-NEC</div>
            <div className="grid grid-cols-3 gap-2">
              <select
                className="input-clean w-full"
                data-testid="vsu-tax-type-select"
                value={form.tax_id_type}
                onChange={(e) => setForm({ ...form, tax_id_type: e.target.value, tax_id: (form.tax_id || "").replace(/\D/g, "") })}
              >
                {ENTITY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              <input
                type="text"
                inputMode="numeric"
                className="input-clean w-full font-mono col-span-2"
                data-testid="vsu-tax-id-input"
                value={formatTaxId(form.tax_id, form.tax_id_type)}
                onChange={(e) => setForm({ ...form, tax_id: (e.target.value || "").replace(/\D/g, "").slice(0, 9) })}
                placeholder={form.tax_id_type === "ssn" ? "XXX-XX-XXXX" : "XX-XXXXXXX"}
                autoComplete="off"
              />
            </div>
            <div className="text-[11px] text-slate-400 mt-1 flex items-center gap-1">
              <ShieldCheck className="w-3 h-3" />
              Encrypted at rest. Only the landlord and their tax preparer can see this — never displayed in listings.
            </div>
          </div>
          <Field label="Notes (optional)">
            <textarea
              rows={2}
              className="input-clean w-full"
              data-testid="vsu-notes-input"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="License #, COI expiration, anything else the landlord should know"
            />
          </Field>
          <div className="flex justify-end pt-2">
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary-dark text-sm flex items-center gap-2 disabled:opacity-60"
              data-testid="vsu-submit-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              {submitting ? "Submitting…" : "Submit my profile"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <label className="block">
      <div className="overline text-slate-500 mb-1">
        {label}{required && <span className="text-rose-500 ml-0.5">*</span>}
      </div>
      {children}
      {hint && <div className="text-[11px] text-slate-400 mt-1">{hint}</div>}
    </label>
  );
}
